import notification from "antd/es/notification";
import mimeTypes from "mime-types";
import { Task } from "redux-saga";
import {
  call,
  cancel,
  cancelled,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLeading,
} from "redux-saga/effects";

import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";

import getYourDownloadWillStartShortlyNode from "@app/component/preview/util/getYourDownloadWillStartShortlyNode";
import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import FileCache from "@app/store/files/FileCache";
import { getOrganization } from "@app/store/iam";
import { downloadFileByUrl } from "@app/util/file";

import { handleError } from "../errors/actions";

import { createFile, downloadFile, fetchFile, fetchFileMetadata, updateFile } from "./actions";

export function* onDownloadFile(action: ReturnType<typeof downloadFile>) {
  const { entityId, entityType, fileId, fileName } = action.payload;
  const notificationKey = `download_file_${fileId}`;
  // Surface the "Downloading…" toast only if the request hasn't returned in
  // 2s — small files (the common case) finish quickly and don't need a toast.
  let pendingToastTimer: ReturnType<typeof setTimeout> | undefined;

  try {
    const organization: Organization = yield select(getOrganization);

    const file: RawFile = yield callApi(
      "fetchFile",
      organization.id,
      fileId,
      false,
      false,
      entityType,
      entityId
    );

    pendingToastTimer = setTimeout(() => {
      notification.info({
        duration: 0, // closed explicitly below — large files can take minutes
        key: notificationKey,
        message: getYourDownloadWillStartShortlyNode(i18nService.getIntl(), fileName ?? file.name),
      });
    }, 2000);

    // timeout: 0 — the default 30s axios timeout kills downloads of large
    // files (250 MB blobs can easily exceed that).
    const fileBlob: Blob = yield callApi(
      "fetchFile",
      organization.id,
      fileId,
      true,
      false,
      entityType,
      entityId,
      { responseType: "blob", timeout: 0 }
    );

    clearTimeout(pendingToastTimer);
    notification.close(notificationKey);

    const fileExtension: string = mimeTypes.extension(file.contentType) || "";

    yield call(
      downloadFileByUrl,
      window.URL.createObjectURL(fileBlob),
      fileName ? `${fileName}.${fileExtension}` : file.name
    );
  } catch (error) {
    if (pendingToastTimer) {
      clearTimeout(pendingToastTimer);
    }
    notification.close(notificationKey);
    yield put(handleError({ error }));
  }
}

export function* onCreateFile({
  payload: { callback, file },
}: ReturnType<typeof createFile.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const uploadedFile: RawFile = yield callApi("createFile", organization.id, file, true);
    if (callback) {
      yield call(callback, uploadedFile);
    }
    yield put(createFile.success(uploadedFile));
  } catch (error) {
    yield put(createFile.failure(error));
    yield put(handleError({ error }));
  }
}

const fileCache = new FileCache(100 * 1024 * 1024); // 100MB cache

export function* onFetchFileMetadata({ payload }: ReturnType<typeof fetchFileMetadata>) {
  const { entityId, entityType, fileId, onFailure, onSuccess } = payload;
  try {
    const organization: Organization = yield select(getOrganization);
    const file: RawFile = yield callApi(
      "fetchFile",
      organization.id,
      fileId,
      false,
      false,
      entityType,
      entityId
    );
    yield call(onSuccess, file);
  } catch (error) {
    if (onFailure) {
      yield call(onFailure);
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onFetchFile({ payload }: ReturnType<typeof fetchFile>) {
  const { entityId, entityType, fileId, onFailure, onSuccess } = payload;
  // Threaded into both axios calls below so a saga cancellation (triggered by
  // `takeLatest` when a duplicate dispatch arrives) actually aborts the
  // in-flight HTTP request, freeing the HTTP/2 stream. Without this, a stale
  // 40 MB blob fetch keeps streaming bytes in the background and contends with
  // the latest fetch on the same connection — the duplicate-stream contention
  // is what triggered the proxy's HTTP/2 reset.
  const controller = new AbortController();
  try {
    const organization: Organization = yield select(getOrganization);

    if (!fileCache.hasFile(fileId)) {
      const file: RawFile = yield callApi(
        "fetchFile",
        organization.id,
        fileId,
        false,
        false,
        entityType,
        entityId,
        { signal: controller.signal }
      );

      const fileBlob: Blob = yield callApi(
        "fetchFile",
        organization.id,
        fileId,
        true,
        false,
        entityType,
        entityId,
        { responseType: "blob", signal: controller.signal, timeout: 0 }
      );

      fileCache.addFile(file, fileBlob);
    }

    const { file, fileBlob } = fileCache.getFile(fileId)!;

    yield call(onSuccess, file, fileBlob);
  } catch (error) {
    if (onFailure) {
      yield call(onFailure);
    } else {
      yield put(handleError({ error }));
    }
  } finally {
    const wasCancelled: boolean = yield cancelled();
    if (wasCancelled) {
      controller.abort();
    }
  }
}

export function* onUpdateFile({
  payload: { callback, file },
}: ReturnType<typeof updateFile.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const updatedFile: RawFile = yield callApi("updateFile", organization.id, file);
    if (callback) {
      yield call(callback, updatedFile);
    }
    yield put(updateFile.success());
  } catch (error) {
    yield put(updateFile.failure(error));
    yield put(handleError({ error }));
  }
}

// takeLatest-per-fileId: cancels a prior in-flight fetch only when the new
// dispatch is for the SAME file. Plain `takeLatest` would also cancel
// unrelated concurrent fetches (e.g. several Timeline FileAddedEvent cards
// loading different attachments), which `takeEvery` correctly allowed.
function* watchFetchFile() {
  const inFlight = new Map<RawFile["id"], Task>();
  while (true) {
    const action: ReturnType<typeof fetchFile> = yield take(fetchFile);
    const key = action.payload.fileId;
    const existing = inFlight.get(key);
    if (existing && existing.isRunning()) {
      yield cancel(existing);
    }
    const task: Task = yield fork(onFetchFile, action);
    inFlight.set(key, task);
  }
}

export function* filesSaga() {
  yield takeLeading(downloadFile, onDownloadFile);
  yield takeEvery(createFile.request, onCreateFile);
  yield takeEvery(updateFile.request, onUpdateFile);
  // Repeated dispatches for the same file (React 18 strict-mode double-run,
  // parent re-render that changes the `file` ref, etc.) previously spawned
  // concurrent 40 MB blob fetches on the same HTTP/2 connection and the
  // proxy reset one of the streams; the FE surfaced ERR_HTTP2_PROTOCOL_ERROR.
  // The per-fileId watcher cancels only the prior fetch for the same file
  // (AbortController in onFetchFile aborts the axios call too).
  yield fork(watchFetchFile);
  yield takeEvery(fetchFileMetadata, onFetchFileMetadata);
}
